/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the AppBar component that is displayed at the top of each
 * view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Hook used to create callback functions.
 */
import { useCallback } from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI AppBar component.
 */
import MuiAppBar from '@material-ui/core/AppBar';

/**
 * Material UI Toolbar component.
 */
import Toolbar from '@material-ui/core/Toolbar';

/**
 * Material UI Typography component.
 */
import Typography from '@material-ui/core/Typography';

/**
 * Material UI IconButton component.
 */
import IconButton from '@material-ui/core/IconButton';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faRandom } from '@fortawesome/pro-solid-svg-icons/faRandom';

/**
 * SVG icon definition
 */
import { faSync } from '@fortawesome/pro-solid-svg-icons/faSync';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * The default theme key used to reset theme back to default after it has been randomised.
 */
import { DEFAULT_THEME_KEY } from 'src/style/ThemeManager';

/**
 * Hook used to get access to the theme manager, enables updating of theme.
 */
import useThemeManagerContext from 'src/hooks/useThemeManagerContext';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IAppBarProps {
    title: string;
    showThemeButtons?: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
  title: {
    flexGrow: 1
  },
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const AppBar: React.FunctionComponent<IAppBarProps> = ({
    title,
    showThemeButtons
}) => {
    // Get classnames for styles.
    const classes = useStyles();

    // Get theme manager.
    const themeManager = useThemeManagerContext();

    // Create callback randomise theme function (reduces rerenders)
    const updateTheme = useCallback(() => {
        themeManager.swapTheme('random')
    }, [themeManager]);

    // Create callback reset theme function (reduces rerenders)
    const resetTheme = useCallback(() => {
        themeManager.swapTheme(DEFAULT_THEME_KEY)
    }, [themeManager]);

    return (
        <MuiAppBar position="static">
            <Toolbar>
                <Typography
                    variant="h6"
                    className={classes.title}
                >
                    {title}
                </Typography>
                {
                    showThemeButtons && (
                        <>
                            <IconButton
                                color="inherit"
                                onClick={resetTheme}
                            >
                                <FontAwesomeIcon
                                    icon={faSync}
                                    fixedWidth
                                />
                            </IconButton>
                            <IconButton
                                color="inherit"
                                onClick={updateTheme}
                            >
                                <FontAwesomeIcon
                                    icon={faRandom}
                                    fixedWidth
                                />
                            </IconButton>
                        </>
                    )
                }
            </Toolbar>
        </MuiAppBar>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default AppBar;


// WEBPACK FOOTER //
// ./src/components/AppBar.tsx